import React from 'react';
import { Button, Tooltip } from 'antd';

import SortableList from '../common/SortableList';
import { ARROW_DOWN, ARROW_DOWN_UP, ARROW_LEFT_RIGHT, ARROW_UP, CHART_BAR, PLUS } from '../icons';
import { TILE_HEIGHTS, TILE_WIDTHS } from '../../utils/dashboards';
import DashboardTile, { tileLabel } from './DashboardTile';

/**
 * A tile's ⋯ while arranging (standards §5 "A dashboard arranges, and saves
 * once"): **Size**, **Height**, **Move up**, **Move down** and **Remove**.
 * Move up / Move down are the drag's twin for a pointer that cannot drag and
 * a screen reader. On a phone Arrange is reorder and size only: no Remove.
 * A heading is short only, so its Tall is disabled and says why.
 */
export function tileMenu(tile, { index, count, phone = false, frozen = false, onAction }) {
	// While Done is saving, nothing in the draft moves (the second review's L6).
	if (frozen) {
		return tileMenu(tile, { index, count, phone, onAction }).map((item) => ({
			...item,
			disabled: true,
			title: 'Saving…',
			...(item.children ? { children: item.children.map((child) => ({ ...child, disabled: true })) } : {}),
		}));
	}
	const sizes = TILE_WIDTHS.map(({ w, label }) => ({
		key: `size-${w}`,
		label,
		disabled: tile.w === w,
		title: tile.w === w ? 'This tile is this size now.' : undefined,
		onClick: () => onAction(tile, 'size', w),
	}));
	const heights = TILE_HEIGHTS.map(({ h, label }) => {
		const headingTall = tile.kind === 'text' && h !== 1;
		return {
			key: `height-${h}`,
			label,
			disabled: tile.h === h || headingTall,
			title: headingTall ? 'A heading is one row tall.' : (tile.h === h ? 'This tile is this height now.' : undefined),
			onClick: () => onAction(tile, 'height', h),
		};
	});
	return [
		{ key: 'size', group: 'record', icon: <ARROW_LEFT_RIGHT />, label: 'Size', children: sizes },
		{ key: 'height', group: 'record', icon: <ARROW_DOWN_UP />, label: 'Height', children: heights },
		{ key: 'move-up', group: 'record', icon: <ARROW_UP />, label: 'Move up', disabled: index <= 0, onClick: () => onAction(tile, 'move', -1) },
		{ key: 'move-down', group: 'record', icon: <ARROW_DOWN />, label: 'Move down', disabled: index >= count - 1, onClick: () => onAction(tile, 'move', 1) },
		phone ? null : { key: 'remove', group: 'destructive', glyph: 'remove', label: 'Remove', onClick: () => onAction(tile, 'remove') },
	].filter(Boolean);
}

/**
 * Each tile's name for its grip, its ⋯ and the announcer: its own label, and —
 * when two tiles would read the same (two unavailable charts, two headings of
 * one text) — its place too, so a screen reader can tell them apart (L10).
 */
export function tileLabels(tiles) {
	const list = Array.isArray(tiles) ? tiles : [];
	const base = list.map((tile) => tileLabel(tile));
	const counts = new Map();
	base.forEach((label) => counts.set(label, (counts.get(label) || 0) + 1));
	return new Map(list.map((tile, i) => [tile.id, counts.get(base[i]) > 1 ? `${base[i]}, tile ${i + 1}` : base[i]]));
}

/**
 * The dashboard's grid (charts A6, plan §6.5): 12 columns, rows that wrap,
 * each tile `w` columns wide and short (280px) or tall (576px); on a phone
 * every tile is full width, in order. It is a SortableList (`layout='grid'`)
 * whose grips show only while arranging and whose new order goes to the
 * page's DRAFT (`onReorder`), never straight to a save (ruled d17).
 *
 * While arranging (not on a phone), **Add chart** and **Add heading** sit in
 * the grid after the last tile, never in the header.
 */
export default function DashboardGrid({
	tiles,
	results,
	loading = false,
	collectionId,
	arranging = false,
	phone = false,
	refusals,
	onReorder,
	onAction,
	onAddChart,
	onAddHeading,
	addDisabledReason = null,
	chartContext,
	onEditChart,
	emptyText,
	frozen = false,
	onRetry,
}) {
	const list = Array.isArray(tiles) ? tiles : [];
	const labels = tileLabels(list);
	const labelOf = (tile) => labels.get(tile.id) || tileLabel(tile);
	const adds = arranging && !phone ? (
		<div className='dashboard-grid__add'>
			<Tooltip title={addDisabledReason || null}>
				<Button icon={<CHART_BAR aria-hidden='true' />} onClick={onAddChart} disabled={frozen || Boolean(addDisabledReason)}>Add chart</Button>
			</Tooltip>
			<Tooltip title={addDisabledReason || null}>
				<Button icon={<PLUS aria-hidden='true' />} onClick={onAddHeading} disabled={frozen || Boolean(addDisabledReason)}>Add heading</Button>
			</Tooltip>
		</div>
	) : null;

	if (!list.length && !arranging) return <p className='dashboard-page__empty'>{emptyText}</p>;

	return (
		<div className={`dashboard-grid-wrap${arranging ? ' dashboard-grid-wrap--arranging' : ''}`}>
			{list.length ? (
				<SortableList
					items={list}
					layout='grid'
					reordering={arranging}
					className='dashboard-grid'
					itemClassName={(tile) => `dashboard-tile dashboard-tile--w${tile.w} dashboard-tile--h${tile.kind === 'text' ? 1 : tile.h}`}
					ariaLabel='Dashboard'
					getLabel={labelOf}
					fixed={frozen ? list.map((tile) => tile.id) : undefined}
					onReorder={onReorder}
					renderItem={(tile, { handle, index }) => (
						<DashboardTile
							tile={tile}
							label={labelOf(tile)}
							frozen={frozen}
							result={results ? results.get(tile.id) : null}
							loading={loading}
							collectionId={collectionId}
							arranging={arranging}
							phone={phone}
							handle={handle}
							refusal={refusals ? refusals.get(tile.id) : null}
							menuActions={arranging ? tileMenu(tile, { index, count: list.length, phone, frozen, onAction }) : null}
							onRemove={phone ? undefined : () => onAction(tile, 'remove')}
							chartContext={chartContext}
							onEditChart={onEditChart}
							onRetry={onRetry}
						/>
					)}
				/>
			) : null}
			{adds}
		</div>
	);
}
